// ============================================================================
// ScenarioReviewPanel (F-666 / M-678) — revision CE des scenarios soumis (GC-D)
// ============================================================================
// Port FIDELE du PoC components/salary/scenario-review-panel.tsx (253 L @ SHA
// 7e40e4a). Par scenario soumis : detail des changements avant→apres (delta
// annuel), encart Impact grille / Impact cout reel / Charge employeur, note
// pedagogique, puis approuver/rejeter avec note de decision ; en pied, les 3
// dernieres decisions.
//
// TRANSPOSITION (PLAN-ATTAQUE §GC-D, P-CE4) — strictement :
//  - server component PoC → composant client (les boutons appellent onDecide,
//    la mutation vit dans le tab, useDecideScenario) ;
//  - les 2 `<form action>` (approve/reject) → 2 boutons + un SEUL champ de note
//    PARTAGE par scenario (SPA : la note voyage pour approve ET reject — le
//    serveur DecideSchema l'accepte pour les deux, cf. « reject avec note ») ;
//  - re-skin @gs/ui : Card/Badge locaux (employee/ui) + EmptyState @gs/ui + tokens
//    (Regle #28, zero hex) ; Intl fr-CA en dur (fmtDate/formatCurrency) → helpers
//    locale-aware (format.ts, P-CE18) ; libelles FR → cles i18n FR+EN ;
//  - PII nominative LEGITIME (surface CE admin, meme classe que la grille
//    nominative) — `realByEmail` est calcule SERVEUR (GC-A). Regle #4 : zero emoji.
//
// P-CE2 : pendant une decision, TOUS les boutons sont disabled (anti double-clic ;
// l'idempotence serveur `not_submitted` est la 2e ligne de defense).

import { useState } from 'react';
import { Check, X } from 'lucide-react';

import { EmptyState, GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { Card, Badge } from '../employee/ui';
import { formatCad, formatSignedCad, formatDate } from './format';
import {
  positionAnnual,
  realCostTotals,
  type MemberRealCost,
  type RealScenarioMember,
} from '../../lib/salary/scenario';
import type { SalaryClass } from '../../lib/types/firestore';
import type { PlanningScenario } from '../../hooks/usePlanification';

export interface ScenarioReviewPanelProps {
  submitted: PlanningScenario[];
  recentDecisions: PlanningScenario[];
  classes: SalaryClass[];
  currentYear: number;
  /** Pivot de projection des taux (annee des taux de `classes`). */
  gridYear: number;
  /** Cout reel par email (SERVEUR) — reconcilie l'impact grille avec la masse. */
  realByEmail: Record<string, MemberRealCost>;
  /** Applique la decision (approve/reject) + note (P-CE16 : la note voyage pour les 2). */
  onDecide: (id: string, decision: 'approved' | 'rejected', note: string | null) => void;
  /** Id du scenario en cours de decision (P-CE2 : desactive TOUS les boutons). */
  pendingId: string | null;
}

export function ScenarioReviewPanel({
  submitted,
  recentDecisions,
  classes,
  currentYear,
  gridYear,
  realByEmail,
  onDecide,
  pendingId,
}: ScenarioReviewPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  // Note de decision locale, une par scenario (partagee approve/reject).
  const [notes, setNotes] = useState<Record<string, string>>({});
  const anyPending = pendingId !== null;

  const classLabel = (id: string | null): string =>
    classes.find((c) => c.id === id)?.label ?? id ?? '—';

  // Fragment « {classe} · ech. {echelon} » (echelon '—' si absent).
  const posLabel = (classId: string | null, echelon: number | null): string =>
    t('gest:grilleCe.approbations.classEchelon', {
      class: classLabel(classId),
      echelon: echelon ?? '—',
    });

  // Impact d'un scenario : delta grille (sur les changements) + delta cout reel
  // et charge employeur des employes concernes (VERBATIM PoC impactOf).
  function impactOf(s: PlanningScenario) {
    const gridDelta = s.changes.reduce((sum, c) => {
      const before =
        c.from_class_id !== null && c.from_echelon !== null
          ? positionAnnual(c.from_class_id, c.from_echelon, classes, currentYear, gridYear)
          : null;
      const after = positionAnnual(c.to_class_id, c.to_echelon, classes, currentYear, gridYear);
      if (after === null) return sum;
      return sum + (after - (before ?? 0));
    }, 0);
    const members: RealScenarioMember[] = s.changes.map((c, i) => ({
      id: c.email ?? `v-${i}`,
      before:
        c.from_class_id !== null && c.from_echelon !== null
          ? { class_id: c.from_class_id, echelon: c.from_echelon }
          : null,
      after: { class_id: c.to_class_id, echelon: c.to_echelon },
      virtual: c.virtual,
    }));
    const real = realCostTotals(members, realByEmail, classes, currentYear, gridYear);
    return { gridDelta, real };
  }

  return (
    <div className="flex flex-col gap-4">
      {submitted.length === 0 ? (
        <EmptyState
          title={t('gest:grilleCe.approbations.empty.title')}
          description={t('gest:grilleCe.approbations.empty.description')}
        />
      ) : (
        submitted.map((s) => {
          const busy = pendingId === s.id;
          const note = notes[s.id] ?? '';
          const { gridDelta, real } = impactOf(s);
          return (
            <Card key={s.id}>
              <div className="mb-3 flex flex-row flex-wrap items-center justify-between gap-3">
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-field font-semibold text-slate-800 dark:text-slate-100">
                    {t('gest:grilleCe.approbations.scenarioTitle', { team: s.team_name, year: s.year })}
                  </h3>
                  <p className="text-meta text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.approbations.submittedBy', {
                      date: formatDate(s.submitted_at, locale),
                      email: s.director_email,
                    })}
                  </p>
                </div>
                <Badge tone="info">{t('gest:grilleCe.approbations.pending')}</Badge>
              </div>

              {s.note ? (
                <p className="mb-2 text-field text-slate-500 dark:text-slate-400">« {s.note} »</p>
              ) : null}

              <ul className="flex flex-col">
                {s.changes.map((c, i) => {
                  const before =
                    c.from_class_id !== null && c.from_echelon !== null
                      ? positionAnnual(c.from_class_id, c.from_echelon, classes, currentYear, gridYear)
                      : null;
                  const after = positionAnnual(c.to_class_id, c.to_echelon, classes, currentYear, gridYear);
                  const d = before !== null && after !== null ? after - before : null;
                  return (
                    <li
                      key={i}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 py-2 text-field last:border-b-0 dark:border-slate-700"
                    >
                      <span className="flex items-center gap-2 font-medium text-slate-800 dark:text-slate-100">
                        {c.name}
                        {c.virtual ? <Badge tone="info">{t('gest:grilleCe.approbations.fictif')}</Badge> : null}
                        {!c.virtual && c.from_class_id !== c.to_class_id ? (
                          <Badge tone="warning">{t('gest:grilleCe.approbations.niveauReset')}</Badge>
                        ) : null}
                      </span>
                      <span className="tabular-nums text-slate-500 dark:text-slate-400">
                        {c.virtual
                          ? t('gest:grilleCe.approbations.hire')
                          : posLabel(c.from_class_id, c.from_echelon)}{' '}
                        → {posLabel(c.to_class_id, c.to_echelon)}
                        {' — '}
                        {before !== null ? formatCad(before, locale) : '—'} →{' '}
                        {after !== null ? formatCad(after, locale) : '—'}
                        {d !== null ? (
                          <span className="ml-2">({formatSignedCad(d, locale)})</span>
                        ) : c.virtual && after !== null ? (
                          <span className="ml-2">({formatSignedCad(after, locale)})</span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 p-3 text-field dark:border-slate-700 dark:bg-slate-800/60">
                <span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.approbations.impactGrid')}{' '}
                  </span>
                  <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
                    {formatSignedCad(gridDelta, locale)}
                  </span>
                </span>
                <span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.approbations.impactReal')}{' '}
                  </span>
                  <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
                    {formatSignedCad(real.deltaAnnual, locale)}
                  </span>
                </span>
                <span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {t('gest:grilleCe.approbations.employerCharge')}{' '}
                  </span>
                  <span className="font-medium tabular-nums text-slate-800 dark:text-slate-100">
                    {formatCad(real.charge, locale)}
                  </span>
                </span>
              </div>

              <p className="mt-3 text-meta text-slate-500 dark:text-slate-400">
                {t('gest:grilleCe.approbations.note')}
              </p>

              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="flex min-w-52 flex-1 flex-col gap-1.5">
                  <label htmlFor={`decision-note-${s.id}`} className="sr-only">
                    {t('gest:grilleCe.approbations.decisionNoteAria')}
                  </label>
                  <input
                    id={`decision-note-${s.id}`}
                    type="text"
                    value={note}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [s.id]: e.target.value }))}
                    placeholder={t('gest:grilleCe.approbations.decisionNotePlaceholder')}
                    className={GS_COMPONENTS.input}
                  />
                </div>
                <button
                  type="button"
                  disabled={anyPending}
                  onClick={() => onDecide(s.id, 'approved', note.trim() || null)}
                  className="inline-flex touch-manipulation items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-field font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50 dark:bg-brand-500 dark:hover:bg-brand-600"
                >
                  <Check size={16} aria-hidden="true" />
                  {busy ? t('gest:grilleCe.approbations.deciding') : t('gest:grilleCe.approbations.approve')}
                </button>
                <button
                  type="button"
                  disabled={anyPending}
                  onClick={() => onDecide(s.id, 'rejected', note.trim() || null)}
                  className="inline-flex touch-manipulation items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-field font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                >
                  <X size={16} aria-hidden="true" />
                  {t('gest:grilleCe.approbations.reject')}
                </button>
              </div>
            </Card>
          );
        })
      )}

      {recentDecisions.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-chip font-medium uppercase tracking-wide text-slate-400">
            {t('gest:grilleCe.approbations.recentTitle')}
          </p>
          <ul className="flex flex-col gap-1">
            {recentDecisions.map((s) => (
              <li
                key={s.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-field dark:border-slate-700"
              >
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  {t('gest:grilleCe.approbations.scenarioTitle', { team: s.team_name, year: s.year })}
                </span>
                <span className="flex items-center gap-2">
                  {s.decision_note ? (
                    <span className="text-meta text-slate-500 dark:text-slate-400">« {s.decision_note} »</span>
                  ) : null}
                  <Badge tone={s.status === 'approved' ? 'success' : 'warning'}>
                    {s.status === 'approved'
                      ? t('gest:grilleCe.approbations.decidedApproved', { date: formatDate(s.decided_at, locale) })
                      : t('gest:grilleCe.approbations.decidedRejected', { date: formatDate(s.decided_at, locale) })}
                  </Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
